"use client";

import { c as _c } from "react/compiler-runtime";
import { Mic, MicOff, PhoneOff, ScreenShare, ScreenShareOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { meetingRoom } from "../model/meeting-room";
interface MeetingControlsProps {
  readonly isMuted: boolean;
  readonly listenOnly: boolean;
  readonly isHost: boolean;
  readonly isSharing: boolean;
  readonly shareBlocked: boolean;
  readonly onLeave: () => void;
  readonly onEnd: () => void;
}

/** The mute / leave / end dock — shared by both views. */
export function MeetingControls(t0) {
  const $ = _c(39);
  const {
    isMuted,
    listenOnly,
    isHost,
    isSharing,
    shareBlocked,
    onLeave,
    onEnd
  } = t0;
  let t1;
  if ($[0] !== isMuted) {
    t1 = () => meetingRoom.setMuted(!isMuted);
    $[0] = isMuted;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isMuted ? "Unmute" : "Mute";
  const t3 = listenOnly ? "Listen-only \u2014 you're not publishing audio" : isMuted ? "Unmute" : "Mute";
  const t4 = isMuted || listenOnly ? "bg-red-500/90 text-white hover:bg-red-500" : "bg-white/15 text-white hover:bg-white/20";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("flex h-10 w-10 items-center justify-center rounded-full transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-40", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== isMuted || $[5] !== listenOnly) {
    t6 = isMuted || listenOnly ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />;
    $[4] = isMuted;
    $[5] = listenOnly;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isMuted || $[8] !== listenOnly || $[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <button type="button" onClick={t1} disabled={listenOnly} aria-pressed={isMuted} aria-label={t2} title={t3} data-testid="meeting-mute" className={t5}>{t6}</button>;
    $[7] = isMuted;
    $[8] = listenOnly;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isSharing) {
    t8 = () => isSharing ? meetingRoom.stopScreenShare() : void meetingRoom.startScreenShare();
    $[15] = isSharing;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = isSharing ? "Stop sharing your screen" : "Share your screen";
  const t10 = shareBlocked ? "Someone else is sharing" : isSharing ? "Stop sharing" : "Share your screen";
  const t11 = isSharing ? "bg-[#5651ff] text-white hover:bg-[#4a45e0]" : "bg-white/15 text-white hover:bg-white/20";
  let t12;
  if ($[17] !== t11) {
    t12 = cn("flex h-10 w-10 items-center justify-center rounded-full transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-40", t11);
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== isSharing) {
    t13 = isSharing ? <ScreenShareOff className="h-5 w-5" /> : <ScreenShare className="h-5 w-5" />;
    $[19] = isSharing;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== isSharing || $[22] !== shareBlocked || $[23] !== t10 || $[24] !== t12 || $[25] !== t13 || $[26] !== t8 || $[27] !== t9) {
    t14 = <button type="button" onClick={t8} disabled={shareBlocked} aria-pressed={isSharing} aria-label={t9} title={t10} data-testid="meeting-share" className={t12}>{t13}</button>;
    $[21] = isSharing;
    $[22] = shareBlocked;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
    $[26] = t8;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== onLeave) {
    t15 = <button type="button" onClick={onLeave} data-testid="meeting-leave" className="rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/15 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80">Leave</button>;
    $[29] = onLeave;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== isHost || $[32] !== onEnd) {
    t16 = isHost ? <button type="button" onClick={onEnd} aria-label="End meeting for everyone" title="End for everyone" data-testid="meeting-end" className="flex h-10 items-center gap-1.5 rounded-full bg-red-500 px-3.5 text-sm font-semibold text-white transition-colors hover:bg-red-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80"><PhoneOff className="h-4 w-4" />End</button> : null;
    $[31] = isHost;
    $[32] = onEnd;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t7) {
    t17 = <div className="flex items-center justify-center gap-2 rounded-full bg-white/6 p-1.5 ring-1 ring-white/10">{t7}{t14}{t15}{t16}</div>;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t7;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}